// Unpublished Work © 2022-2024 Deere & Company.

import {datadogLogs} from '@datadog/browser-logs';
import {createPpeIconMap} from 'Utils/icon-utils';
import {formatNumber} from 'Utils/unit-conversion-utils';
import {PRESENTATION_PROPERTIES} from 'Ui/constants/workboard-constants';
import {getProfilePic} from 'Services/user-service';
import {getBase64DataURL} from 'Utils/html-utils';
import React from 'react';
import DetailsBlock from 'OnLabor/my-work/my-work-tile/details-block';
import {fetchUserImageById} from 'OnLabor/workboard/utils/fetch-operators';

const MINUTES_PER_HOUR_FLOAT = 60.0;

const PPE_ICON_MAP = createPpeIconMap();

function getWorkItemsPerColumn(workItems, columnCount) {
    const itemsPerRow = Object.keys(workItems).length / columnCount;

    return Object.keys(workItems).reduce((workItemsPerColumn, item, index) => {
        const chunkIndex = Math.floor(index / itemsPerRow);

        if (!workItemsPerColumn[chunkIndex]) {
            workItemsPerColumn[chunkIndex] = [];
        }

        workItemsPerColumn[chunkIndex].push(item);

        return workItemsPerColumn;
    }, []);
}

function getElementScrollY(elem) {
    return elem.scrollTop;
}

async function getFormattedProfilePic({
    profilePicUrl, isTeamMember, orgId, id
}) {
    const {
        imageData, extension
    } = isTeamMember ? await fetchUserImageById(orgId, id) : await getProfilePic(profilePicUrl);

    return imageData ? getBase64DataURL(`image/${extension}`, imageData) : null;
}

function resolveWorkItemAppUser(workItem, job, teamMembersMap, orgId, translations, isPreviewFixEnabled) {
    const teamMember = teamMembersMap.get(workItem.appUserId);

    if (teamMember) {
        workItem.appUser = {
            ...teamMember,
            color: teamMember?.onLinkUser?.groupColor,
            isTeamMember: true,
            orgId
        };
    } else if (workItem.appUserId && isPreviewFixEnabled) {
        datadogLogs.logger.warn('LABOR - Presentation work item operator not found in team members', {
            appUserId: workItem.appUserId,
            jobId: job.jobId,
            workItemId: workItem.workItemId,
            orgId
        });

        workItem.appUser = workItem.appUser || {
            appUserId: workItem.appUserId,
            firstName: translations.NOT_AVAILABLE,
            lastName: '',
            isTeamMember: false,
            orgId
        };
    }
}

function getRouteForWorkItem(workItem, translations, showRoute) {
    if (!showRoute || !workItem.route) {
        return null;
    }

    if (workItem.routeCustomDefn?.length > 0) {
        return `${translations.ONLINK_ROUTE}: ${workItem.route} - ${translations.ONLINK_HOLES} ${workItem.routeCustomDefn.join(', ')}`;
    }

    return `${translations.ONLINK_ROUTE}: ${workItem.route}`;
}

function buildPresentationWorkItem(job, workItem, translations, properties) {
    const {
        showRoute,
        showPPEIcon,
        showEstimatedHours,
        showEmployeeImage
    } = properties;
    const formattedEquipmentNames =
        workItem.equipment?.filter(({equipmentName}) => equipmentName).map((equipment) =>
            `${equipment.equipmentName} (${equipment.manufacturerName} ${equipment.modelName})`
        );

    return {
        ...workItem,
        appUserName: `${workItem.appUser.firstName} ${workItem.appUser.lastName}`,
        appUserId: workItem.appUserId,
        color: workItem.appUser.color,
        estHours: showEstimatedHours ? formatNumber(workItem.estDuration / MINUTES_PER_HOUR_FLOAT, {
            maximumFractionDigits: 1,
            minimumFractionDigits: 1
        }) : null,
        equipment: workItem.equipment,
        formattedEquipmentNames,
        jobId: job.jobId,
        jobTemplateId: job.jobTemplate?.jobTemplateId,
        note: workItem.note,
        ppeList: showPPEIcon ? job.jobTemplate?.ppeList : [],
        profilePicUrl: showEmployeeImage ?
            getFormattedProfilePic(workItem.appUser) :
            null,
        route: getRouteForWorkItem(workItem, translations, showRoute),
        secondaryName: job.jobTemplate?.secondaryName,
        title: job.title,
        workItemId: workItem.workItemId
    };
}

async function getAllWorkItems(jobs, translations, properties, teamMembersMap = new Map(), orgId, isPreviewFixEnabled) {
    const {showEmployeeImage} = properties;
    const workItems = jobs.flatMap((job) => job.workItems.reduce((filteredWorkItems, workItem) => {
        resolveWorkItemAppUser(workItem, job, teamMembersMap, orgId, translations, isPreviewFixEnabled);

        if (workItem.appUser) {
            filteredWorkItems.push(buildPresentationWorkItem(job, workItem, translations, properties));
        }

        return filteredWorkItems;
    }, []));

    return showEmployeeImage ?
        await Promise.all(workItems.map(
            (workItem) => workItem.profilePicUrl.then((profilePicUrl) => ({
                ...workItem,
                profilePicUrl
            })))
        ) : workItems;
}

function getPresentationSettings(membership) {
    const grouping = membership.properties[PRESENTATION_PROPERTIES.grouping];
    const columnCount = membership.properties[PRESENTATION_PROPERTIES.columnCount];
    const fontSize = membership.properties[PRESENTATION_PROPERTIES.fontSize];
    const showEstimatedHours = membership.properties[PRESENTATION_PROPERTIES.showEstimatedHours] === 'true';
    const showPPE = membership.properties[PRESENTATION_PROPERTIES.showPPE] === 'true';
    const tileOpacity = membership.properties[PRESENTATION_PROPERTIES.tileOpacity] === 'true' ? 'opacity-on' : 'opacity-off';
    const showRoute = membership.properties[PRESENTATION_PROPERTIES.showRoute] === 'true';
    const showEmployeeGroupColor = membership.properties[PRESENTATION_PROPERTIES.showEmployeeGroupColor] === 'true';
    const showEmployeeImage = membership.properties[PRESENTATION_PROPERTIES.showEmployeeImage] === 'true';

    return {
        grouping,
        columnCount,
        fontSize,
        showEstimatedHours,
        showPPE,
        tileOpacity,
        showRoute,
        showEmployeeGroupColor,
        showEmployeeImage
    };
}

function getPresentationUrl(membershipId) {
    const {onLinkLegacyUri: baseUri} = window.props;

    return `${baseUri}/labor/workboard/presentation/${membershipId}`;
}

function getWorkItemEquipmentDetails(equipment) {
    return equipment?.map((equipment, index) => (
        <div
            className='workitem-equipment-details'
            key={index}
        >
            {`${equipment?.equipmentName} (${equipment?.manufacturerName} ${equipment?.modelName})`}
        </div>
    ));
}

function getSharedWorkItemDetails(workItem, showPPE, showSecondaryName) {
    const equipmentList = getWorkItemEquipmentDetails(workItem.equipment);
    const hasSecondaryName = Boolean(workItem.secondaryName);

    return (
        <>
            {
                hasSecondaryName && showSecondaryName &&
                <div>{`(${workItem.secondaryName})`}</div>
            }
            {equipmentList}
            {workItem.route && <div>{workItem.route}</div>}
            {workItem.note && <div
                style={{
                    whiteSpace: 'pre-line'
                }}
            >{workItem.note}</div>}
            {
                showPPE && workItem.ppeList?.length > 0 &&
                <DetailsBlock>
                    {
                        workItem.ppeList?.map((ppeName) => PPE_ICON_MAP[ppeName])
                    }
                </DetailsBlock>
            }
        </>
    );
}

export {
    getElementScrollY,
    getWorkItemsPerColumn,
    getAllWorkItems,
    getPresentationSettings,
    getPresentationUrl,
    getSharedWorkItemDetails
};
